import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { notBlank } from "../util/ext";
import {
  compose,
  equals,
  filter,
  find,
  identity,
  prop,
  propEq,
  sortBy,
  toLower,
  not,
  is,
} from "ramda";
import TextField from "@mui/material/TextField";
import { useOrganizations } from "./userServices";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import { matchSorter } from "match-sorter";
import { notUUID } from "../util/ext";
import { locality } from "../util/organizationUtil";
import VirtualizedListbox, {
  LISTBOX_BORDER_STYLE,
} from "../components/VirtualizedListbox";

const filterOptions = (options, { inputValue }) =>
  matchSorter(options, inputValue, {
    keys: [
      "organizationId",
      "organizationName",
      "location.locality",
      "location.administrativeArea",
    ],
  });

const OrganizationAutoComplete = props => {
  const {
    onChange,
    onBlur,
    value,
    label = "Organization",
    filterFunction = identity,
    valueProp = "organizationId",
    activeOnly = false,
    autoFocus = false,
    disabled = false,
    freeSolo = false,
    required = false,
    multiple = false,
    TextFieldProps = {},
    helperText,
    error,
  } = props;
  const organizations = useOrganizations();
  const [activeOrganizations, setActiveOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(
    multiple ? [] : null,
  );

  const filterFunctionCallback = useCallback(filterFunction, [filterFunction]);

  useEffect(() => {
    if (organizations.isSuccess) {
      const orgList = sortBy(
        compose(toLower, prop("organizationName")),
        filter(
          org => !activeOnly || org.active,
          filter(filterFunctionCallback, organizations.data),
        ),
      );

      setActiveOrganizations(orgList);

      if (multiple) {
        const ids = Array.isArray(value) ? value : [];
        setSelectedOrganization(
          ids.map(
            id =>
              find(propEq(valueProp, id), orgList) || {
                [valueProp]: id,
                organizationName: id,
                active: true,
              },
          ),
        );
      } else {
        const selected = find(propEq(valueProp, value), orgList);
        // if value is not UUID, assume it is an org name
        if (notBlank(value) && notUUID(value)) {
          setSelectedOrganization({ organizationName: value, active: true });
        } else {
          setSelectedOrganization(selected);
        }
      }
    }
  }, [
    activeOnly,
    filterFunctionCallback,
    multiple,
    organizations.data,
    organizations.isSuccess,
    valueProp,
    value,
  ]);

  const handleOrganizationSelect = useCallback(
    (_event, selection) => {
      if (multiple) {
        const orgs = Array.isArray(selection) ? selection : [];
        setSelectedOrganization(orgs);
        onChange(orgs.map(org => org[valueProp]).filter(Boolean));
        return;
      }

      // if organization is string and autocomplete is freeSolo
      // assume organization is organizationName and add to orgList
      const org = is(String, selection)
        ? { organizationName: selection, active: true }
        : selection;

      if (is(String, selection)) {
        setActiveOrganizations(prev => [...prev, org]);
      }

      setSelectedOrganization(org);
      onChange(org ? org[valueProp] : null);
    },
    [multiple, onChange, valueProp],
  );

  return (
    <Fragment>
      {organizations.isLoading || organizations.isStale ? (
        <Skeleton variant="text" />
      ) : (
        <Autocomplete
          multiple={multiple}
          freeSolo={freeSolo}
          disabled={disabled}
          disableListWrap
          filterOptions={filterOptions}
          options={activeOrganizations}
          value={selectedOrganization || null}
          ListboxComponent={VirtualizedListbox}
          ListboxProps={{
            itemSize: 80,
            listStyle: LISTBOX_BORDER_STYLE,
          }}
          getOptionLabel={org => notBlank(org) && `${org.organizationName}`}
          isOptionEqualToValue={(opt, val) =>
            notBlank(val) && equals(opt.organizationId, val.organizationId)
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box width="100%">
                <Typography
                  noWrap
                  style={{ fontWeight: "bold" }}
                  color={not(option.active) ? "error" : "inherit"}
                >
                  {option.organizationName}
                </Typography>

                {option?.location && (
                  <Typography color={not(option.active) ? "error" : "inherit"}>
                    {locality(option)}
                  </Typography>
                )}
                {option.organizationId && (
                  <Typography color={not(option.active) ? "error" : "inherit"}>
                    {`(${option.organizationId})`}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          onChange={handleOrganizationSelect}
          onBlur={onBlur}
          renderInput={params => {
            params.inputProps["aria-autocomplete"] = "none";
            params.inputProps["id"] = null;
            return (
              <TextField
                {...params}
                required={required}
                autoFocus={autoFocus}
                type="text"
                name={"program-organization-" + Math.random()}
                variant={TextFieldProps?.variant ?? "standard"}
                label={label}
                error={error}
                helperText={helperText}
                fullWidth
              />
            );
          }}
        />
      )}
    </Fragment>
  );
};

OrganizationAutoComplete.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  filterFunction: PropTypes.func,
  valueProp: PropTypes.string,
  activeOnly: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  freeSolo: PropTypes.bool,
  required: PropTypes.bool,
  multiple: PropTypes.bool,
  TextFieldProps: PropTypes.object,
  helperText: PropTypes.string,
  error: PropTypes.bool,
};

export default OrganizationAutoComplete;
